// frontend/src/components/BulkJobProgressStrip.js
import React, { useEffect, useMemo, useRef } from "react";
import { Box, Flex, Progress, Text, Badge, IconButton, Icon } from "@chakra-ui/react";
import { CloseIcon, CheckCircleIcon } from "@chakra-ui/icons";

const KIND_LABEL = { welcome: "welcome", approval: "approval" };

// How long to leave a successful "Sent N …" confirmation visible before
// auto-clearing the strip. Long enough for the admin to register the result,
// short enough that it doesn't masquerade as an in-flight job.
const AUTO_DISMISS_MS = 5000;

function formatRelative(seconds) {
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  const m = Math.ceil(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

export default function BulkJobProgressStrip({ job, onDismiss }) {
  const total = job?.total || 0;
  const sent = job?.sent || 0;
  const failed = job?.failed || 0;
  const done = sent + failed;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const kindLabel = KIND_LABEL[job?.kind] || job?.kind || "";
  const isComplete = !!job?.completed_at;
  const isBlocked = !!job?.blocked;
  const hasFailures = failed > 0;
  const successComplete = isComplete && !hasFailures && !isBlocked;

  const status = useMemo(() => {
    if (isBlocked) return "Halted — Zoho block detected";
    if (job?.rate_wait_until) {
      const seconds = Math.max(0, job.rate_wait_until * 1000 - Date.now()) / 1000;
      return `Waiting for Zoho rate window to reset · ${formatRelative(seconds)}`;
    }
    return null;
  }, [isBlocked, job?.rate_wait_until]);

  // Auto-dismiss only fully-successful completions. Failures or Zoho blocks
  // stay sticky so the admin notices and can act. Guarded with a ref so
  // unrelated parent re-renders don't keep restarting the timer.
  const autoDismissedRef = useRef(false);
  useEffect(() => {
    if (autoDismissedRef.current) return;
    if (!successComplete || !onDismiss) return;
    autoDismissedRef.current = true;
    const id = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [successComplete, onDismiss]);

  if (!job) return null;

  const bg = successComplete ? "green.50" : isBlocked ? "red.50" : "blue.50";
  const borderColor = successComplete ? "green.200" : isBlocked ? "red.200" : "blue.200";
  const progressScheme = successComplete ? "green" : isBlocked ? "red" : "blue";
  const badgeScheme = isBlocked
    ? "red"
    : successComplete
      ? "green"
      : isComplete && hasFailures
        ? "orange"
        : "blue";

  let headerText;
  if (successComplete) {
    headerText = `Sent ${sent} ${kindLabel} email${sent === 1 ? "" : "s"}`;
  } else if (isComplete) {
    headerText = `Finished sending ${kindLabel} emails`;
  } else {
    headerText = `Sending ${kindLabel} emails`;
  }

  return (
    <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3} mb={3}>
      <Flex align="center" gap={3} mb={2}>
        {successComplete && <Icon as={CheckCircleIcon} color="green.500" />}
        <Text fontWeight="bold">{headerText}</Text>
        <Badge colorScheme={badgeScheme}>
          {sent} sent · {failed} failed · {total} total
        </Badge>
        {status && <Text color="orange.600" fontSize="sm">{status}</Text>}
        {isComplete && onDismiss && (
          <IconButton
            ml="auto"
            size="xs"
            variant="ghost"
            aria-label="Dismiss"
            icon={<CloseIcon boxSize={2} />}
            onClick={onDismiss}
          />
        )}
      </Flex>
      <Progress
        value={isComplete ? 100 : pct}
        colorScheme={progressScheme}
        size="sm"
        hasStripe={!isComplete}
        isAnimated={!isComplete && !isBlocked}
      />
    </Box>
  );
}
